import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Rocket, Sparkles, CheckCircle2, Mail, AlertTriangle, Loader2, Circle } from "lucide-react";

const STEPS = [
  { id: "ciblage", label: "Construction du ciblage par l'IA (secteurs, décideurs)" },
  { id: "recherche", label: "Recherche d'entreprises réelles dans votre zone (SIRENE)" },
  { id: "import", label: "Vérification des doublons et exclusions, ajout en base" },
  { id: "envoi", label: "Rédaction des e-mails personnalisés et lancement" },
];
const STEP_ORDER = ["ciblage", "recherche", "import", "envoi", "termine"];

export const ExpressProspectionDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ service: "", zone: "", nombre: 10 });
  const [entites, setEntites] = useState([]);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) api.get("/entites").then(({ data }) => setEntites(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!job || job.statut !== "en_cours") return;
    let errors = 0;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/prospection/express/${job.id}`);
        errors = 0;
        setJob({ ...data, id: job.id });
        if (data.statut === "termine") setTimeout(() => setResult(data.result), 1200);
        if (data.statut !== "en_cours") clearInterval(t);
      } catch {
        errors += 1;
        if (errors >= 5) {
          clearInterval(t);
          toast.error("Impossible de suivre la recherche — vérifiez votre connexion et réessayez");
          setJob(null);
        }
      }
    }, 2000);
    return () => clearInterval(t);
  }, [job?.id, job?.statut]);

  const launch = async () => {
    if (!form.service.trim() || !form.zone.trim()) {
      toast.error("Indiquez ce que vous vendez et la zone");
      return;
    }
    try {
      const { data } = await api.post("/prospection/express", {
        service: form.service, zone: form.zone, nombre: Number(form.nombre) || 10,
      });
      setJob({ id: data.job_id, statut: "en_cours", etape: "ciblage", detail: "Démarrage…" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const close = () => { setResult(null); setJob(null); setForm({ service: "", zone: "", nombre: 10 }); onClose(); };
  const loading = job && job.statut === "en_cours";
  const failed = job && job.statut === "erreur";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="express-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Rocket size={18} className="text-accent" /> Démarrer ma prospection</DialogTitle>
        </DialogHeader>

        {!result && !loading && !failed && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Répondez à 3 questions : l'IA construit le ciblage, trouve des entreprises réelles dans votre zone (SIRENE),
              rédige les e-mails personnalisés et lance la séquence — sans rien d'autre à faire.
            </p>
            <div>
              <Label>1. Que vendez-vous ?</Label>
              <Input
                className="mt-1"
                list="express-services"
                placeholder="Ex : nettoyage de bureaux, plateaux repas, hôtesses d'accueil…"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                data-testid="express-service-input"
              />
              <datalist id="express-services">
                {entites.flatMap((e) => (e.services || []).map((s) => <option key={`${e.id}-${s}`} value={`${e.nom} — ${s}`} />))}
              </datalist>
            </div>
            <div>
              <Label>2. Dans quelle zone ?</Label>
              <Input
                className="mt-1"
                placeholder="Ville, département (ex : 69) ou code postal"
                value={form.zone}
                onChange={(e) => setForm({ ...form, zone: e.target.value })}
                data-testid="express-zone-input"
              />
            </div>
            <div>
              <Label>3. Combien d'entreprises contacter ? (max 50)</Label>
              <Input
                className="mt-1 w-28"
                type="number" min={1} max={50}
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                data-testid="express-count-input"
              />
            </div>
            <Button className="w-full" size="lg" onClick={launch} data-testid="express-launch-button">
              <Sparkles size={16} className="mr-2" /> Lancer ma prospection
            </Button>
            <p className="text-xs text-muted-foreground">Dès qu'une entreprise répond, les relances s'arrêtent et vous reprenez la main.</p>
          </div>
        )}

        {loading && (
          <div className="space-y-3 py-4" data-testid="express-loading">
            <div className="flex items-center gap-2">
              <Loader2 size={20} className="animate-spin text-accent" />
              <p className="text-sm font-semibold">Recherche en cours — ne fermez pas cette fenêtre</p>
            </div>
            <div className="space-y-2.5 rounded-md border p-3">
              {STEPS.map((s) => {
                const cur = STEP_ORDER.indexOf(job.etape);
                const idx = STEP_ORDER.indexOf(s.id);
                const done = idx < cur;
                const active = idx === cur;
                return (
                  <div key={s.id} className="flex items-start gap-2 text-sm" data-testid={`express-step-${s.id}`}>
                    {done ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />
                      : active ? <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-accent" />
                      : <Circle size={16} className="mt-0.5 shrink-0 text-muted-foreground/40" />}
                    <span className={done ? "text-emerald-700" : active ? "font-medium" : "text-muted-foreground"}>{s.label}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="express-step-detail">{job.detail}</p>
          </div>
        )}

        {failed && (
          <div className="space-y-3 py-4" data-testid="express-error">
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="flex items-center gap-2 text-sm font-medium text-red-800"><AlertTriangle size={15} /> {job.detail}</p>
            </div>
            <Button className="w-full" variant="outline" onClick={() => setJob(null)} data-testid="express-retry-button">Réessayer</Button>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="express-result">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800"><CheckCircle2 size={16} /> {result.message}</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md border p-3"><p className="font-display text-2xl font-semibold">{result.entreprises_trouvees}</p><p className="text-xs text-muted-foreground">trouvées (SIRENE)</p></div>
              <div className="rounded-md border p-3"><p className="font-display text-2xl font-semibold">{result.importees}</p><p className="text-xs text-muted-foreground">ajoutées en base</p></div>
              <div className="rounded-md border p-3"><p className="font-display text-2xl font-semibold text-emerald-600">{result.contactes}</p><p className="text-xs text-muted-foreground">contactées</p></div>
            </div>
            <p className="text-sm"><span className="text-muted-foreground">Campagne créée :</span> <span className="font-medium">{result.campagne.nom}</span></p>
            {result.a_completer?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="express-missing-emails">
                <p className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <AlertTriangle size={15} /> {result.a_completer.length} entreprise(s) sans e-mail (SIRENE ne les fournit pas)
                </p>
                <p className="mt-1 text-xs text-amber-700">Trouvez leur adresse (site web, appel) puis complétez la fiche : elles seront contactées au prochain lancement.</p>
                <ul className="mt-2 max-h-32 space-y-0.5 overflow-y-auto text-xs">
                  {result.a_completer.map((p, i) => <li key={i}>• {p.raison_sociale} {p.ville && `(${p.ville})`}</li>)}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <Button asChild className="flex-1" data-testid="express-goto-prospects"><Link to="/prospects" onClick={close}><Mail size={15} className="mr-1.5" /> Voir les prospects</Link></Button>
              <Button variant="outline" className="flex-1" onClick={close} data-testid="express-close-button">Fermer</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
